import parsePhoneNumber from 'libphonenumber-js/max';
import countries from 'shared/constants/countries.js';

export const CONTACT_ENTITY_TYPES = {
  PHONE: 'phone',
  EMAIL: 'email',
};

export const DEFAULT_PHONE_DIAL_CODE = '+55';
export const DEFAULT_PHONE_COUNTRY_CODE = '55';

export const normalizePhoneForLookup = value =>
  `${value || ''}`.replace(/\D/g, '');

export const normalizeEmailForLookup = value =>
  `${value || ''}`.trim().toLowerCase();

export const normalizeContactEntityValue = (type, value) => {
  if (type === CONTACT_ENTITY_TYPES.EMAIL) {
    return normalizeEmailForLookup(value);
  }

  return normalizePhoneForLookup(value);
};

const buildBrazilianLocalPhoneVariants = digits => {
  if (!digits) {
    return [];
  }

  if (digits.length === 11 && digits[2] === '9') {
    return [digits, `${digits.slice(0, 2)}${digits.slice(3)}`];
  }

  if (digits.length === 10 && ['6', '7', '8', '9'].includes(digits[2])) {
    return [digits, `${digits.slice(0, 2)}9${digits.slice(2)}`];
  }

  return [digits];
};

export const buildPhoneLookupVariants = (
  value,
  defaultCountryCode = DEFAULT_PHONE_COUNTRY_CODE
) => {
  const digits = normalizePhoneForLookup(value);
  if (!digits) {
    return [];
  }

  const variants = new Set([digits]);
  const hasDefaultCountryCode = digits.startsWith(defaultCountryCode);
  const localDigits = hasDefaultCountryCode
    ? digits.slice(defaultCountryCode.length)
    : digits;

  buildBrazilianLocalPhoneVariants(localDigits).forEach(localVariant => {
    variants.add(localVariant);
    variants.add(`${defaultCountryCode}${localVariant}`);
  });

  return Array.from(variants).filter(Boolean);
};

export const buildContactEntitySearchTerms = (type, value) => {
  if (type === CONTACT_ENTITY_TYPES.EMAIL) {
    const normalizedEmail = normalizeEmailForLookup(value);
    return normalizedEmail ? [normalizedEmail] : [];
  }

  return buildPhoneLookupVariants(value);
};

export const doesContactMatchEntity = (type, contact, value) => {
  if (!contact) {
    return false;
  }

  if (type === CONTACT_ENTITY_TYPES.EMAIL) {
    return (
      normalizeEmailForLookup(contact.email) === normalizeEmailForLookup(value)
    );
  }

  const contactDigits = normalizePhoneForLookup(contact.phone_number);
  if (!contactDigits) {
    return false;
  }

  const variants = buildPhoneLookupVariants(value);
  return variants.some(
    variant =>
      contactDigits === variant ||
      contactDigits.endsWith(variant) ||
      variant.endsWith(contactDigits)
  );
};

export const doesContactExactlyMatchEntity = (type, contact, value) => {
  if (!contact) {
    return false;
  }

  if (type === CONTACT_ENTITY_TYPES.EMAIL) {
    return (
      normalizeEmailForLookup(contact.email) === normalizeEmailForLookup(value)
    );
  }

  return (
    normalizePhoneForLookup(contact.phone_number) ===
    normalizePhoneForLookup(value)
  );
};

export const buildContactEntityFilterPayload = (type, value) => {
  const normalizedValue = normalizeContactEntityValue(type, value);
  const attributeKey =
    type === CONTACT_ENTITY_TYPES.EMAIL ? 'email' : 'phone_number';
  const filterOperator =
    type === CONTACT_ENTITY_TYPES.EMAIL ? 'contains' : 'contains';

  return {
    payload: [
      {
        attribute_key: attributeKey,
        filter_operator: filterOperator,
        values: [normalizedValue],
        attribute_model: 'standard',
        custom_attribute_type: '',
      },
    ],
  };
};

export const buildPhoneInputState = (
  value,
  fallbackDialCode = DEFAULT_PHONE_DIAL_CODE
) => {
  const trimmedValue = `${value || ''}`.trim();
  if (!trimmedValue) {
    return {
      phoneNumber: '',
      activeDialCode: '',
    };
  }

  const digitsOnly = normalizePhoneForLookup(trimmedValue);
  if (!digitsOnly) {
    return {
      phoneNumber: '',
      activeDialCode: '',
    };
  }

  const normalizedPhoneNumber =
    trimmedValue.startsWith('+') || digitsOnly.startsWith('55')
      ? `+${digitsOnly}`
      : digitsOnly;

  const parsedPhoneNumber = normalizedPhoneNumber.startsWith('+')
    ? parsePhoneNumber(normalizedPhoneNumber)
    : null;

  const matchedDialCode = normalizedPhoneNumber.startsWith('+')
    ? countries
        .map(country => country.dial_code)
        .filter(Boolean)
        .sort((left, right) => right.length - left.length)
        .find(dialCode => normalizedPhoneNumber.startsWith(dialCode))
    : '';

  const hasParsedCountryCode = Boolean(
    parsedPhoneNumber?.countryCallingCode && parsedPhoneNumber?.nationalNumber
  );
  const hasMatchedDialCode = Boolean(
    !hasParsedCountryCode &&
      matchedDialCode &&
      normalizedPhoneNumber.length > matchedDialCode.length
  );

  return {
    phoneNumber: hasParsedCountryCode
      ? `${parsedPhoneNumber.nationalNumber}`
      : hasMatchedDialCode
      ? normalizedPhoneNumber.slice(matchedDialCode.length)
      : normalizedPhoneNumber,
    activeDialCode: parsedPhoneNumber?.countryCallingCode
      ? `+${parsedPhoneNumber.countryCallingCode}`
      : hasMatchedDialCode
      ? matchedDialCode
      : fallbackDialCode,
  };
};
